import { useState } from 'react';
import {
  ComponentRendering,
  GetStaticComponentProps,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Image from 'next/image';

import { icon_arrow_right, icon_chevron_right, icon_close_large } from '@gn-audio/icons';
import {
  Button,
  ComponentProps,
  JabraIcon,
  useFallbackTranslation,
} from '@gn-audio/jss-components';

import { getProductsByANCType, PIMProduct, SearchResults } from 'api/productCatalogueApi';
import { ancTypes, ancTypesList } from 'composables/Product/ANCTypes/ANCTypesConstants';
import { ANCTypesProps } from 'composables/Product/ANCTypes/ANCTypesTypes';
import { ANCTypesButtons } from 'composables/Product/Buy/ANCTypes/ANCTypesButtons';
import { ANCTypesLabels } from 'composables/Product/Buy/ANCTypes/ANCTypesLabels';

const animationClassName = 'transition-colors ease-linear duration-500';

const ANCTypes = ({ fields, names, uniqueProductsByFamilyList }: ANCTypesProps) => {
  const [activeType, setActiveType] = useState<ancTypes>(
    (String(fields.activeItem.value) || ancTypes.first) as ancTypes
  );
  const [expanded, setExpanded] = useState(false);
  const { t, getURLWithLocalePrefix } = useFallbackTranslation();

  const handleTypeClick = (type: ancTypes) => {
    if (!fields.static.value) {
      setActiveType(type);
    }
  };

  return (
    <div className="light bg-beige-50">
      <div className="container">
        <div className="flex flex-col items-center">
          <span className="text-base font-normal uppercase leading-5">
            {fields.featureTitle.value}
          </span>
          <span className="my-4 h-0.5 w-[26px] bg-accent" />
          <h2 className="text-center text-[2rem] font-bold leading-8 xxs:text-5xl xxs:leading-[4.125rem]">
            {fields.title.value}
          </h2>
          <p className="mb-10 mt-4 text-center text-xl font-light leading-6">
            {fields.paragraph.value}
          </p>

          <div className="relative flex w-full flex-row items-center xxs:flex-col">
            <div className="absolute bottom-0 left-0 ml-3 flex w-[349px] origin-bottom-left -rotate-90 flex-row items-center xxs:static xxs:mb-[22px] xxs:w-full xxs:rotate-0 xs:px-5 lg:px-9 xl:px-[52px]">
              <span>{fields.indicatorLabel?.value}</span>
              <div className="-mr-1.5 ml-2 h-0 flex-grow border-t-[1px] border-dashed" />
              <JabraIcon className="h-[11px] w-[11px]" Icon={icon_chevron_right} />
            </div>

            <ANCTypesButtons
              activeType={activeType}
              animationClassName={animationClassName}
              ancTypesList={ancTypesList}
              fields={fields}
              onClick={handleTypeClick}
            />

            <ANCTypesLabels
              names={names}
              activeType={activeType}
              fields={fields}
              ancTypesList={ancTypesList}
            />
          </div>

          <h3 className="pb-6 pt-[58px] text-center text-[0.938rem] font-normal leading-5 text-gray-700 xxs:text-2xl xxs:leading-[1.875rem]">
            {fields[`descriptionType${activeType}`]?.value}
          </h3>

          <Button
            variant="secondary"
            size="m"
            icon={<JabraIcon Icon={expanded ? icon_close_large : icon_arrow_right} />}
            iconPlacement="right"
            onClick={() => setExpanded((prevState) => !prevState)}
          >
            {t('system.LearnMore')}
          </Button>

          {expanded && (
            <p
              className="pt-6 text-center text-[0.938rem] font-normal leading-5 text-gray-700 xxs:text-2xl xxs:leading-[1.875rem]"
              dangerouslySetInnerHTML={{
                __html: fields[`extendedDescriptionType${activeType}`]?.value,
              }}
            />
          )}

          {!fields.static.value && (
            <div className="w-full pt-10">
              <h2 className="w-full text-2xl font-normal text-gray-700">
                {fields.titleRecommendedProducts.value}
              </h2>
              <div className="mt-8 flex flex-wrap gap-2">
                {uniqueProductsByFamilyList[Number(activeType) - 1]?.map((product) => {
                  return (
                    <a
                      className="flex w-full flex-col items-center justify-center rounded-3xl bg-beige-100 p-8 !no-underline transition-colors duration-300 ease-linear hover:bg-beige-50 hover:shadow-[0_4px_4px_rgba(0,0,0,0.15)] md:w-1/3"
                      key={`anc-spot-recommended-product-${product.familyName}`}
                      href={getURLWithLocalePrefix(product.pageUrl)}
                    >
                      <h3 className="text-center text-[1.4rem] font-normal leading-6 md:text-lg">
                        {product.familyName}
                      </h3>
                      <Image
                        className="w-full max-w-[300px]"
                        src={`https:${product.images[0]}`}
                        alt={product.familyName}
                        width={300}
                        height={300}
                        loading="lazy"
                      />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering: ComponentRendering) => {
  try {
    const productsData = await Promise.all(ancTypesList.map((type) => getProductsByANCType(type)));

    const names = productsData.reduce((acc, data) => {
      if (!data.count) {
        return acc;
      }

      const name = (data as SearchResults).items.find((el) => el.familyName)?.familyName as string;
      return [...acc, name];
    }, []);

    const uniqueProductsByFamilyList = productsData.reduce((acc, data) => {
      if (!data.count) {
        return acc;
      }

      const uniqueAncProductsByFamily = (data as SearchResults).items.reduce(
        (itemsAcc: PIMProduct[], item: PIMProduct) => {
          const alreadyExist = itemsAcc.find((accItem) => item.familyName === accItem.familyName);

          return alreadyExist ? itemsAcc : [...itemsAcc, item];
        },
        []
      );

      return [...acc, uniqueAncProductsByFamily];
    }, []);

    return {
      uniqueProductsByFamilyList,
      names,
      fields: rendering.fields ?? null,
    };
  } catch (error) {
    console.error(error);
    return {
      uniqueProductsByFamilyList: [],
      names: [],
      fields: [],
    };
  }
};

export default withDatasourceCheck()<ComponentProps>(ANCTypes);
